import React from 'react';
import { DollarSign, TrendingUp, Shield, Info } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

const Cryptocurrencies: React.FC = () => {
  useSEO({
    title: 'Como Investir em Criptomoedas e ETFs de Bitcoin em 2026 | O Teu Dinheiro',
    description: 'Guia prático sobre Bitcoin, Ethereum e ETFs de criptomoedas na B3 (HASH11, BITH11). Entenda como investir com segurança e declarar no Imposto de Renda.',
    keywords: 'criptomoedas 2026, como investir em bitcoin, ETF bitcoin B3, HASH11, BITH11, ethereum, segurança cripto',
    url: 'https://www.oteudinheiro.com.br/criptomoedas'
  });
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-yellow-600 to-orange-600 text-white py-20">
        <div className="container-custom text-center">
          <DollarSign className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Criptomoedas
          </h1>
          <p className="text-xl md:text-2xl text-yellow-100 max-w-3xl mx-auto">
            Entenda Bitcoin, Ethereum e outras moedas digitais
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Column */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                O que são criptomoedas?
              </h2>

              <div className="prose prose-lg text-gray-600 mb-8">
                <p className="mb-4">
                  Criptomoedas são moedas digitais que usam criptografia para garantir
                  a segurança das transações e controlar a criação de novas unidades.
                </p>
                <p className="mb-4">
                  Elas operam em uma tecnologia chamada blockchain, que é um registro
                  público e descentralizado de todas as transações.
                </p>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Principais criptomoedas
              </h3>

              <div className="space-y-6 mb-8">
                <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-yellow-500">
                  <h4 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                    <DollarSign className="w-5 h-5 text-yellow-600 mr-2" />
                    Bitcoin (BTC)
                  </h4>
                  <p className="text-gray-600 mb-3">
                    A primeira e mais conhecida criptomoeda. Criada em 2009, é considerada
                    o "ouro digital" e serve como reserva de valor.
                  </p>
                  <ul className="text-sm text-gray-500 space-y-1">
                    <li>• Primeira criptomoeda</li>
                    <li>• Maior capitalização de mercado</li>
                    <li>• Limitado a 21 milhões de unidades</li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
                  <h4 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                    <TrendingUp className="w-5 h-5 text-blue-600 mr-2" />
                    Ethereum (ETH)
                  </h4>
                  <p className="text-gray-600 mb-3">
                    Plataforma que permite criar aplicações descentralizadas (dApps)
                    e contratos inteligentes usando blockchain.
                  </p>
                  <ul className="text-sm text-gray-500 space-y-1">
                    <li>• Plataforma de contratos inteligentes</li>
                    <li>• Base para DeFi e NFTs</li>
                    <li>• Transição para proof-of-stake</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div>
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Vantagens das criptomoedas
                </h3>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="bg-green-100 p-2 rounded-lg">
                      <Shield className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Segurança</h4>
                      <p className="text-gray-600 text-sm">Transações criptografadas e seguras</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Potencial de crescimento</h4>
                      <p className="text-gray-600 text-sm">Alto risco, mas alto retorno potencial</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="bg-purple-100 p-2 rounded-lg">
                      <Info className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Descentralização</h4>
                      <p className="text-gray-600 text-sm">Não controlado por bancos centrais</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Risks Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Riscos e cuidados
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="warning-box">
              <h3 className="text-xl font-semibold text-accent-900 mb-3">
                Alta volatilidade
              </h3>
              <p className="text-accent-700">
                Criptomoedas podem ter variações de preço muito grandes em pouco tempo.
                O valor pode cair drasticamente de um dia para o outro.
              </p>
            </div>

            <div className="warning-box">
              <h3 className="text-xl font-semibold text-accent-900 mb-3">
                Falta de regulamentação
              </h3>
              <p className="text-accent-700">
                O mercado ainda não é totalmente regulamentado, o que pode trazer
                riscos para investidores desprotegidos.
              </p>
            </div>

            <div className="warning-box">
              <h3 className="text-xl font-semibold text-accent-900 mb-3">
                Segurança das carteiras
              </h3>
              <p className="text-accent-700">
                Se você perder o acesso à sua carteira digital, pode perder
                todo o dinheiro investido permanentemente.
              </p>
            </div>

            <div className="warning-box">
              <h3 className="text-xl font-semibold text-accent-900 mb-3">
                Falta de garantias
              </h3>
              <p className="text-accent-700">
                Diferente de bancos tradicionais, não há proteção ao investidor
                ou garantias em caso de perdas.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How to Invest Section */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Como investir com segurança
          </h2>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-yellow-600">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Estude muito
              </h3>
              <p className="text-gray-600">
                Entenda como funcionam as criptomoedas antes de investir qualquer dinheiro.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-yellow-600">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Comece pequeno
              </h3>
              <p className="text-gray-600">
                Investiga apenas o que você pode perder sem comprometer suas finanças.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-yellow-600">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Diversifique
              </h3>
              <p className="text-gray-600">
                Não coloque todo seu dinheiro em uma só criptomoeda.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-yellow-600">4</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Use exchanges confiáveis
              </h3>
              <p className="text-gray-600">
                Escolha plataformas regulamentadas e com boa reputação.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-yellow-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-6">
            Investir em criptomoedas requer conhecimento!
          </h2>
          <p className="text-xl text-yellow-100 mb-8 max-w-2xl mx-auto">
            Estude muito antes de investir e nunca invista mais do que pode perder.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/blog/etf-bitcoin-bolsa-vale-pena"
              className="btn bg-white text-yellow-700 hover:bg-gray-100 font-bold"
            >
              Ver Guia: ETF de Criptomoedas na B3 (HASH11, BITH11)
            </a>
            <a
              href="/investimentos"
              className="btn bg-transparent text-white border-2 border-white hover:bg-white hover:text-yellow-600"
            >
              Ver outros investimentos
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Cryptocurrencies;
